import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CodeUsageHistory } from '@/types';
import { History, Gift, Ticket } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface CodeUsageHistoryCardProps {
  userId: string;
}

export const CodeUsageHistoryCard = ({ userId }: CodeUsageHistoryCardProps) => {
  const [history, setHistory] = useState<CodeUsageHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const historyRef = collection(db, 'users', userId, 'codeUsageHistory');
        const q = query(historyRef, orderBy('appliedAt', 'desc'));
        const snapshot = await getDocs(q);
        
        console.log('Code usage history snapshot size:', snapshot.size);
        console.log('Code usage history docs:', snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as CodeUsageHistory[];
        
        console.log('Parsed code usage history:', data);
        setHistory(data);
      } catch (error) {
        console.error('Error fetching code usage history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <History className="w-6 h-6 text-gray-600" />
          コード利用履歴
        </h2>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <History className="w-6 h-6 text-gray-600" />
        コード利用履歴
      </h2>

      {history.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>まだコードを利用していません</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {item.codeType === 'invite' ? (
                    <Gift className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Ticket className="w-5 h-5 text-purple-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        item.codeType === 'invite'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {item.codeType === 'invite' ? '招待コード' : 'キャンペーンコード'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {format(item.appliedAt.toDate(), 'yyyy/MM/dd HH:mm', { locale: ja })}
                    </span>
                  </div>
                  <div className="font-mono text-sm font-semibold text-gray-900 mb-2">
                    {item.code}
                  </div>
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">プラン:</span>
                      <span>
                        {item.planBefore}
                        <span className="mx-2">→</span>
                        <span className="font-semibold text-green-600">{item.planAfter}</span>
                      </span>
                    </div>
                    {item.endDateAfter && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-500">終了日:</span>
                        <span>
                          {format(item.endDateAfter.toDate(), 'yyyy/MM/dd', { locale: ja })}
                        </span>
                      </div>
                    )}
                    {item.rewardDays !== undefined && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-500">付与:</span>
                        <span className="font-semibold text-blue-600">{item.rewardDays}日間</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
